import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { App as AntApp } from 'antd';
import AppBar from '../components/AppBar';
import { Chip, Skeletons, initials } from '../components/kit';
import * as contactsApi from '../api/contacts';
import { nationalityLabel } from '../lib/nationalities';

const GET_FN = {
  vendor: contactsApi.getVendorsById,
  purchaser: contactsApi.getPurchasersById,
  coagency: contactsApi.getCoAgenciesById,
};

const titleCase = (s) => (s ? String(s).toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : null);

// A linked deal is tappable when the server gives us the underlying row id.
// Sales open the sale detail; co-agency letters open the letter detail.
const dealHref = (l) => {
  const rid = l?.id || l?.sale_id || l?.letter_id;
  if (!rid) return null;
  return l.role === 'Letter' ? `/letters/${rid}` : `/sales/${rid}`;
};

export default function ContactDetailPage() {
  const { type, id } = useParams();
  const nav = useNavigate();
  const { message } = AntApp.useApp();
  const [d, setD] = useState(null);

  useEffect(() => {
    (GET_FN[type] || contactsApi.getVendorsById)(id)
      .then((r) => setD(r?.data ?? r))
      .catch((e) => message.error(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, id]);

  const regLabel = d?.entity_type === 'INDIVIDUAL' ? 'IC / passport no' : 'Registration no';

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        back
        title={d?.name || 'Contact'}
        sub={titleCase(d?.sub_type) || d?.entity_type}
        right={d && (d.can_edit ?? d.is_mine !== false) && (
          // The server's own answer: a landlord / tenant in your book, or a co-agency
          // you registered (an older server sends only is_mine).
          <span onClick={() => nav(`/contacts/${type}/${id}/edit`)} style={{ cursor: 'pointer', color: 'var(--primary)', fontSize: 13, fontWeight: 600, padding: '0 6px' }}>Edit</span>
        )}
      />
      {!d ? (
        <div style={{ padding: 18 }}><Skeletons n={3} height={70} /></div>
      ) : (
        <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '15px 16px' }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--chip)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flex: 'none' }}>{initials(d.name)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>{d.name}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                {d.entity_type && <Chip label={d.entity_type} />}
                <Chip label={d.code ? 'In ERP' : 'Pending ERP'} tone={d.code ? 'ok' : 'warn'} />
                {d.is_mine === false && d.creator_name && <Chip label={`by ${d.creator_name}`} tone="brand" />}
              </div>
            </div>
          </div>

          <Card>
            <KV k={regLabel} v={d.registration_no || '—'} mono />
            {d.entity_type === 'INDIVIDUAL' && <KV k="Nationality" v={nationalityLabel(d.nationality) || '—'} />}
            {d.board_register_no != null && <KV k="Board reg no" v={d.board_register_no || '—'} mono />}
            <KV k="Contact person" v={d.contact_person || '—'} />
            <KV k="Phone" v={d.phone || '—'} />
            {type === 'coagency' && <KV k="Email" v={d.email || '—'} />}
            <KV k="Address" v={d.address || '—'} />
            <KV k="Debtor / creditor code" v={d.code || '—'} mono />
          </Card>

          <div style={sectionLabel}>BANK ACCOUNTS</div>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', marginTop: -4 }}>
            {(d.bank_accounts || []).length === 0 ? (
              <div style={{ padding: '15px 16px', fontSize: 13, color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>No bank accounts on file.</div>
            ) : (
              d.bank_accounts.map((b, i) => (
                <div key={b.id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 16px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{b.bank} · <span style={{ fontFamily: 'ui-monospace,monospace' }}>{b.acct}</span></div>
                    <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{b.name}</div>
                  </div>
                  {b.is_primary && <Chip label="Primary" tone="ok" />}
                </div>
              ))
            )}
            {(d.can_edit_banks ?? d.is_mine !== false) && (
              <div onClick={() => nav(`/contacts/${type}/${id}/edit`)} style={{ padding: '14px 16px', fontSize: 13, fontWeight: 600, color: 'var(--primary)', cursor: 'pointer' }}>＋ Add bank account</div>
            )}
          </div>

          <div style={sectionLabel}>LINKED DEALS</div>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', marginTop: -4 }}>
            {(d.linked_deals || []).length === 0 ? (
              <div style={{ padding: '15px 16px', fontSize: 13, color: 'var(--muted)' }}>Not linked to any of your deals yet.</div>
            ) : (
              d.linked_deals.map((l, i) => {
                const href = dealHref(l);
                return (
                  <div
                    key={l.ref || i}
                    onClick={href ? () => nav(href) : undefined}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, padding: '15px 16px', fontSize: 13, cursor: href ? 'pointer' : 'default', borderBottom: i < d.linked_deals.length - 1 ? '1px solid var(--border)' : 'none' }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <span style={{ fontFamily: 'ui-monospace,monospace', fontWeight: 600 }}>{l.ref}</span>
                      {l.role && <span style={{ marginLeft: 8, fontSize: 11.5, color: 'var(--muted)' }}>{l.role}</span>}
                    </div>
                    {/* Only offer "Open" when there is somewhere to go — a row without an
                        id used to render a link that silently did nothing when tapped. */}
                    {href && <span style={{ color: 'var(--primary)', fontWeight: 600, flex: 'none' }}>Open ›</span>}
                  </div>
                );
              })
            )}
          </div>

          <div style={{ background: 'var(--surface)', border: '1.5px dashed var(--border)', borderRadius: 14, padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5, fontWeight: 600, letterSpacing: '.07em', color: 'var(--muted)' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <rect x="5" y="11" width="14" height="9" rx="2" />
                <path d="M8 11 V8 a4 4 0 0 1 8 0 v3" />
              </svg>
              HQ-ONLY · READ-ONLY
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>Debtor / creditor code validation and the ERP push are done by HQ finance.</div>
          </div>
        </div>
      )}
    </div>
  );
}

const sectionLabel = { fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)' };
const Card = ({ children }) => <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '6px 14px' }}>{children}</div>;
const KV = ({ k, v, mono }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
    <span style={{ fontSize: 13, color: 'var(--muted)' }}>{k}</span>
    <span style={{ fontSize: 13, fontWeight: 600, fontFamily: mono ? 'ui-monospace,monospace' : 'inherit', textAlign: 'right', wordBreak: 'break-word' }}>{v}</span>
  </div>
);
